import React, { useState, useRef, useEffect } from "react";
import { BaseEditorProps } from "./BaseEditor";

// Browser compatibility check
const checkBrowserSupport = () => {
  const userAgent = navigator.userAgent;
  const isChrome =
    /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);

  return { isChrome, isFirefox, isSafari, isEdge };
};

const PythonPlayground: React.FC<BaseEditorProps> = ({
  code = "",
  onChange,
  readOnly = false,
}) => {
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState("");
  const [browserInfo] = useState(checkBrowserSupport());

  const editorRef = useRef<HTMLTextAreaElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  // Enhanced auto-resize for textarea
  useEffect(() => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const adjustHeight = () => {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.max(200, textarea.scrollHeight)}px`;
    };

    textarea.addEventListener("input", adjustHeight);
    adjustHeight(); // Initial adjustment

    return () => {
      textarea.removeEventListener("input", adjustHeight);
    };
  }, []);

  // Enhanced code execution with better error handling
  const runCode = async () => {
    const currentCode = code || "";
    if (!currentCode.trim()) {
      setError("Please enter some Python code to run");
      return;
    }

    setIsRunning(true);
    setOutput("");
    setError("");

    try {
      // Check if browser supports Worker
      if (typeof Worker === "undefined") {
        throw new Error(
          "Your browser doesn't support Web Workers. Please use a modern browser for code execution."
        );
      }

      // Simulate Python execution (replace with actual Python interpreter)
      // For now, we'll simulate with a setTimeout
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Enhanced output simulation with better formatting
      const simulatedOutput = await simulatePythonExecution(currentCode);
      setOutput(simulatedOutput);
    } catch (err: any) {
      console.error("Code execution error:", err);
      setError(err.message || "An error occurred while running your code");
    } finally {
      setIsRunning(false);
    }
  };

  // Enhanced Python simulation with better parsing
  const simulatePythonExecution = async (
    codeInput: string
  ): Promise<string> => {
    const lines = codeInput.split("\n");
    const outputs: string[] = [];
    let variables: { [key: string]: any } = {};

    try {
      for (const line of lines) {
        const trimmedLine = line.trim();

        // Skip comments and empty lines
        if (!trimmedLine || trimmedLine.startsWith("#")) continue;

        // Handle print statements
        if (trimmedLine.includes("print(")) {
          const printMatch = trimmedLine.match(/print\((.*)\)/);
          if (printMatch) {
            let content = printMatch[1];

            // Handle f-strings
            if (content.startsWith('f"') || content.startsWith("f'")) {
              content = content.substring(2, content.length - 1);
              content = content.replace(/\{([^}]+)\}/g, (match, expr) => {
                try {
                  // Simple variable substitution
                  if (variables[expr]) {
                    return variables[expr].toString();
                  }
                  return match;
                } catch {
                  return match;
                }
              });
            } else if (content.startsWith('"') || content.startsWith("'")) {
              // Regular string
              content = content.substring(1, content.length - 1);
            } else {
              // Variable or expression
              if (variables[content]) {
                content = variables[content].toString();
              }
            }

            outputs.push(content);
          }
        }

        // Handle simple variable assignments
        else if (trimmedLine.includes("=") && !trimmedLine.includes("==")) {
          const [varName, value] = trimmedLine.split("=").map((s) => s.trim());

          // Parse simple values
          if (!isNaN(Number(value))) {
            variables[varName] = Number(value);
          } else if (value.startsWith("[") && value.endsWith("]")) {
            // Simple list parsing
            try {
              const listContent = value.substring(1, value.length - 1);
              const items = listContent.split(",").map((item) => {
                const trimmed = item.trim();
                return !isNaN(Number(trimmed)) ? Number(trimmed) : trimmed;
              });
              variables[varName] = items;
            } catch {
              variables[varName] = value;
            }
          } else {
            variables[varName] = value;
          }
        }
      }

      return outputs.join("\n") || "Code executed successfully (no output)";
    } catch (error) {
      throw new Error(`Execution error: ${error}`);
    }
  };

  // Enhanced keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const currentCode = code || "";

    // Ctrl/Cmd + Enter to run
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      runCode();
      return;
    }

    // Tab for indentation
    if (e.key === "Tab") {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newCode =
        currentCode.substring(0, start) + "    " + currentCode.substring(end);
      onChange?.(newCode);

      // Set cursor position after the inserted spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      }, 0);
    }

    // Auto-indent on Enter
    if (e.key === "Enter") {
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const lines = currentCode.substring(0, start).split("\n");
      const currentLine = lines[lines.length - 1];

      // Count leading spaces for indentation
      const indent = currentLine.match(/^\s*/)?.[0] || "";

      // Add extra indent for lines ending with ":"
      const extraIndent = currentLine.trim().endsWith(":") ? "    " : "";

      e.preventDefault();
      const newCode =
        currentCode.substring(0, start) +
        "\n" +
        indent +
        extraIndent +
        currentCode.substring(textarea.selectionEnd);
      onChange?.(newCode);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd =
          start + 1 + indent.length + extraIndent.length;
      }, 0);
    }
  };

  // Clear output
  const clearOutput = () => {
    setOutput("");
    setError("");
  };

  // Copy code to clipboard
  const copyCode = async () => {
    const currentCode = code || "";
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(currentCode);
        alert("Code copied to clipboard!");
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = currentCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        alert("Code copied to clipboard!");
      }
    } catch (err) {
      console.error("Failed to copy code:", err);
      alert("Failed to copy code. Please manually select and copy.");
    }
  };

  const displayCode = code || "";

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Python Playground
          </h2>
          {!browserInfo.isChrome && !browserInfo.isFirefox && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              For best experience, use Chrome or Firefox
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={copyCode}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            title="Copy code to clipboard"
          >
            <i className="fas fa-copy mr-1"></i>
            Copy
          </button>

          <button
            onClick={runCode}
            disabled={isRunning || readOnly}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              isRunning || readOnly
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md"
            }`}
            title="Run code (Ctrl+Enter)"
          >
            {isRunning ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1"></i>
                Running...
              </>
            ) : (
              <>
                <i className="fas fa-play mr-1"></i>
                Run Code
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Code Editor */}
        <div className="flex-1 flex flex-col">
          <div className="bg-gray-800 text-gray-200 px-4 py-2 text-sm flex justify-between items-center">
            <span>Code Editor</span>
            <span className="text-xs text-gray-400">
              Press Ctrl+Enter to run • Tab for indent
            </span>
          </div>

          <div className="flex-1 relative">
            <textarea
              ref={editorRef}
              value={displayCode}
              onChange={(e) => onChange?.(e.target.value)}
              onKeyDown={handleKeyDown}
              readOnly={readOnly}
              className="w-full h-full p-4 pl-16 font-mono text-sm bg-gray-900 text-gray-100 border-none outline-none resize-none"
              placeholder="Enter your Python code here..."
              spellCheck={false}
              style={{
                lineHeight: "1.5",
                tabSize: 4,
                minHeight: "200px",
              }}
            />

            {/* Line numbers */}
            <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-800 border-r border-gray-700 flex flex-col text-xs text-gray-500 pt-4">
              {displayCode.split("\n").map((_, index) => (
                <div
                  key={index}
                  className="h-6 flex items-center justify-end pr-2"
                >
                  {index + 1}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Output Panel */}
        <div className="w-1/2 flex flex-col border-l">
          <div className="bg-gray-100 px-4 py-2 text-sm flex justify-between items-center border-b">
            <span className="font-medium">Output</span>
            <button
              onClick={clearOutput}
              className="text-xs text-gray-600 hover:text-gray-800 transition-colors"
              title="Clear output"
            >
              <i className="fas fa-trash mr-1"></i>
              Clear
            </button>
          </div>

          <div
            ref={outputRef}
            className="flex-1 p-4 bg-white overflow-auto font-mono text-sm"
            style={{ minHeight: "200px" }}
          >
            {isRunning ? (
              <div className="text-blue-600 flex items-center">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Executing code...
              </div>
            ) : error ? (
              <div className="text-red-600 whitespace-pre-wrap">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {error}
              </div>
            ) : output ? (
              <div className="text-gray-800 whitespace-pre-wrap">{output}</div>
            ) : (
              <div className="text-gray-400 italic">
                Output will appear here when you run your code
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer with tips */}
      <div className="bg-gray-50 border-t px-4 py-2 text-xs text-gray-600">
        <div className="flex justify-between items-center">
          <span>💡 Tip: Use Ctrl+Enter to run code, Tab for indentation</span>
          <span>
            Browser:{" "}
            {browserInfo.isChrome
              ? "Chrome"
              : browserInfo.isFirefox
              ? "Firefox"
              : browserInfo.isSafari
              ? "Safari"
              : browserInfo.isEdge
              ? "Edge"
              : "Other"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PythonPlayground;
